import styled from "styled-components";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { ProcessOrderHeader } from "@components";
import {
    useAppSelector,
    AppDispatch,
    selectRedeemer,
    fetchRedeemer,
    createNewRedeemer,
    selectBookNow,
    setBookNowItemData,
    selectCheckout,
} from "@redux";
import { AppModuleLayout } from "@layouts";
import { breakpoint } from "@utilities";
import { LoadingStage } from "src/Interfaces/shared";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { BookNowBreadcrumbs } from "@routes";
import { ICartItemData, IPayNowResponseData, ISetCustomerData } from "@interfaces";
import _ from "lodash";

import { cartApi } from "@api";
import { BookNowStepLabels, buildBookNowCheckoutContext, CheckoutCartStepLabels } from "src/Module/Checkout/checkoutHelpers";
import { CheckoutStep2 } from "src/Module/Checkout/Components/CheckoutStep2";
import { CheckoutStep1 } from "src/Module/Checkout/Components/CheckoutStep1";
import { CheckoutProvider } from "../../Module/Checkout/CheckoutContext";

export const BookNow = () => {
    //page hook
    const dispatch = useDispatch<AppDispatch>();
    //redux state
    const { activeDiscount } = useAppSelector(selectCheckout);
    const { cartItems, directPurchaseItemData, cartItemsLoadingStage, } =
        useAppSelector(selectBookNow);
    const cartCustomers = useAppSelector(selectRedeemer);
    //page state
    const [activeStepIndex, setActiveStep] = useState(0);

    useEffect(() => {
        if (cartItemsLoadingStage === LoadingStage.INITIALIZE_FAILURE) {
            ErrorToast({ Message: "Failed to load items" });
        }
    }, [cartItemsLoadingStage]);

    const handleSaveItems = async (items: ICartItemData[]) => {
        await dispatch(setBookNowItemData(items));
    };

    const onNextStep = async (items: ICartItemData[], nextStep: string) => {
        await handleSaveItems(items);
        setActiveStep(
            _.findIndex(CheckoutCartStepLabels, (label) => label.trim().toLowerCase() === nextStep),
        );
    };

    const handleOrderCreate = async (paymentType: string) => {
        const purchaseDirectResponse = await cartApi.purchaseDirect({
            ...directPurchaseItemData!,
        });
        if (purchaseDirectResponse.status !== 200) {
            console.error(purchaseDirectResponse);
            throw new Error();
        }
        if (paymentType === "pay-now") {
            const quoteUrl = (
                purchaseDirectResponse.data.data as IPayNowResponseData
            ).payNow.redirectUrl.replaceAll("\\", "");
            window.location.href = quoteUrl;
        } else {
            setActiveStep(BookNowStepLabels.length - 1);
        }
    };

    const handleHeaderBack = () => {
        if (activeStepIndex > 0) {
            setActiveStep(activeStepIndex - 1);
            return;
        }
        window.history.back();
    };

    const onCreateNewCustomer = async (newCustomerData: ISetCustomerData) => {
        const newCustomer = await dispatch(createNewRedeemer(newCustomerData)).unwrap();
        await dispatch(fetchRedeemer()).unwrap();
        return newCustomer;
    };

    const renderComponent = () => {
        const activeStep = BookNowStepLabels[activeStepIndex];
        switch (activeStep) {
            case "Checkout":
                return (
                    <CheckoutStep2
                        items={cartItems}
                        moveTo={setActiveStep}
                        customers={cartCustomers.redeemer}
                        onItemsSave={(items) => handleSaveItems(items)}
                        onCreateNewCustomer={onCreateNewCustomer}
                        handleOrderCreate={handleOrderCreate}
                    />
                );
            case "Item":
                return (
                    <CheckoutStep1
                        handleSaveItems={(items) => onNextStep(items, "checkout")}
                    />
                );
        }
    };

    useEffect(() => {
        dispatch(fetchRedeemer());
    }, []);

    return (
        <AppModuleLayout onBack={handleHeaderBack}>
            <ProcessOrderHeader
                stepLabels={BookNowStepLabels}
                activeStepIndex={activeStepIndex}
                breadcrumbs={BookNowBreadcrumbs}
                changeStep={setActiveStep}
            />
            <Main>
                <CheckoutProvider
                    initialItems={cartItems}
                    checkoutContext={buildBookNowCheckoutContext()}
                    activeDiscount={activeDiscount?.discount}
                >
                    {renderComponent()}
                </CheckoutProvider>
            </Main>
        </AppModuleLayout>
    );
};

const Main = styled.div`
    flex: 1;
    align-items: center;
    background-color: #f2f2f2;

    ${breakpoint.breakTablet`
        padding-bottom: 30px;
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
    `}
`;
